import { createApi, retry } from '@reduxjs/toolkit/query/react';
import { axiosBaseQuery, getHeaders } from '@urc-ui/store/api/base';
import { HttpMethod, Tag } from '@urc-ui/store/api/types';
import { GetCampaignsRequest } from '@flexible-reports/store/api/campaigns/api-types';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { GetCampaignsResponseForReact } from '@flexible-reports/store/api/campaigns/types';
import { transformFromQueryCampaigns } from '@flexible-reports/store/api/campaigns/transformers/transform-from-query-campaigns';
import { logger } from '@flexible-reports/services/logger';
import { exponentialBackoffWithJitter } from '@flexible-reports/store/api/base';

export const campaignsApi = createApi({
  reducerPath: 'campaignsApi',
  baseQuery: retry(
    axiosBaseQuery({
      baseUrl: '/reporting/api'
    }),
    exponentialBackoffWithJitter({ baseDelay: 50, maxDelay: 5000, maxRetries: 5 })
  ),
  tagTypes: [Tag.CAMPAIGNS],
  endpoints: builder => ({
    getCampaigns: builder.query<GetCampaignsResponseForReact, GetCampaignsRequest>({
      query: (request: GetCampaignsRequest) => ({
        url: '/query/campaigns',
        method: HttpMethod.POST,
        data: request,
        headers: getHeaders()
      }),
      transformResponse: transformFromQueryCampaigns,
      providesTags: [Tag.CAMPAIGNS]
    }),
    getCampaignsInfinite: builder.query<GetCampaignsResponseForReact, GetCampaignsRequest>({
      query: (request: GetCampaignsRequest) => ({
        url: '/query/campaigns',
        method: HttpMethod.POST,
        data: request,
        headers: getHeaders()
      }),
      serializeQueryArgs: ({ queryArgs }) => {
        const { advertiserAccountIds, managerAccountIds, searchTerm } = queryArgs;
        return { advertiserAccountIds, managerAccountIds, searchTerm };
      },
      merge: (currentCache, newItems, { arg }) => ({
        ...currentCache,
        campaigns: arg.paginationToken ? [...currentCache.campaigns, ...newItems.campaigns] : newItems.campaigns,
        nextPaginationToken: newItems.nextPaginationToken,
        totalResults: newItems.totalResults
      }),
      transformResponse: transformFromQueryCampaigns,
      providesTags: [Tag.CAMPAIGNS]
    })
  })
});

export const { useGetCampaignsQuery, useGetCampaignsInfiniteQuery, useLazyGetCampaignsQuery, useLazyGetCampaignsInfiniteQuery } =
  campaignsApi;

export const useInfiniteCampaigns = (baseRequest: Omit<GetCampaignsRequest, 'paginationToken'>, options?: { skip?: boolean }) => {
  const [trigger, { data, isLoading, isError, isFetching }] = useLazyGetCampaignsInfiniteQuery();
  const [isSearchLoading, setIsSearchLoading] = useState(false);
  const [currentSearchKey, setCurrentSearchKey] = useState('');
  const [lastSuccessfulSearchKey, setLastSuccessfulSearchKey] = useState('');

  const advertiserIdsKey = useMemo(() => baseRequest.advertiserAccountIds.join(','), [baseRequest.advertiserAccountIds]);
  const managerAccountIdsKey = useMemo(() => baseRequest.managerAccountIds.join(','), [baseRequest.managerAccountIds]);
  const searchKey = useMemo(
    () => `${advertiserIdsKey}-${managerAccountIdsKey}-${baseRequest.searchTerm || ''}`,
    [advertiserIdsKey, managerAccountIdsKey, baseRequest.searchTerm]
  );

  useEffect(() => {
    let isCurrent = true;
    const searchCampaigns = async () => {
      if (!options?.skip) {
        setCurrentSearchKey(searchKey);
        setIsSearchLoading(true);
        try {
          const result = await trigger({ ...baseRequest, paginationToken: null });
          if (isCurrent && !result.isError) {
            setLastSuccessfulSearchKey(searchKey);
          }
        } catch (error) {
          if (isCurrent) {
            logger.logErrorMessage('Failed to search campaigns:', error);
          }
        } finally {
          if (isCurrent) {
            setIsSearchLoading(false);
          }
        }
      }
    };
    searchCampaigns();

    return () => {
      isCurrent = false;
    };
  }, [baseRequest.searchTerm, advertiserIdsKey, managerAccountIdsKey]);

  const fetchNextPage = useCallback(
    async (customPageSize?: number) => {
      if (data?.nextPaginationToken) {
        try {
          await trigger({
            ...baseRequest,
            pageSize: customPageSize || baseRequest.pageSize,
            paginationToken: data.nextPaginationToken
          }).unwrap();
        } catch (error) {
          logger.logErrorMessage('Failed to fetch next page:', error);
          throw error;
        }
      }
    },
    [data?.nextPaginationToken, baseRequest, trigger]
  );

  const shouldShowData = currentSearchKey === lastSuccessfulSearchKey;

  const refresh = useCallback(async () => {
    await trigger({ ...baseRequest, paginationToken: null });
  }, [trigger, baseRequest]);

  return {
    campaigns: shouldShowData ? data?.campaigns || [] : [],
    isLoading: isLoading || isSearchLoading,
    isFetching,
    isError,
    hasNextPage: shouldShowData ? !!data?.nextPaginationToken : false,
    fetchNextPage,
    totalResults: shouldShowData ? data?.totalResults || 0 : 0,
    refresh: refresh
  };
};
